'use client';

import React, { CSSProperties, forwardRef } from 'react';
import classNames from 'classnames';

import {
  GridProps,
  SpacingProps,
  SizeProps,
  StyleProps,
  CommonProps,
  DisplayProps,
  ConditionalProps,
} from '../interfaces';
import { SpacingToken, ColorScheme, ColorWeight } from '../types';

interface ComponentProps
  extends GridProps,
    SpacingProps,
    SizeProps,
    StyleProps,
    CommonProps,
    DisplayProps,
    ConditionalProps {}

const Grid = forwardRef<HTMLDivElement, ComponentProps>(
  (
    {
      as: Component = 'div',
      inline,
      columns,
      gap,
      position = 'relative',
      aspectRatio,
      align,
      textVariant,
      textSize,
      textWeight,
      textType,
      tabletColumns,
      mobileColumns,
      padding,
      paddingLeft,
      paddingRight,
      paddingTop,
      paddingBottom,
      paddingX,
      paddingY,
      margin,
      marginLeft,
      marginRight,
      marginTop,
      marginBottom,
      marginX,
      marginY,
      dark,
      light,
      width,
      height,
      maxWidth,
      minWidth,
      minHeight,
      maxHeight,
      top,
      right,
      bottom,
      left,
      fit,
      fill,
      fillWidth = false,
      fillHeight = false,
      fitWidth,
      fitHeight,
      hide,
      show,
      background,
      solid,
      opacity,
      transition,
      pointerEvents,
      border,
      borderTop,
      borderRight,
      borderBottom,
      borderLeft,
      borderStyle,
      borderWidth,
      radius,
      topRadius,
      rightRadius,
      bottomRadius,
      leftRadius,
      topLeftRadius,
      topRightRadius,
      bottomLeftRadius,
      bottomRightRadius,
      overflow,
      overflowX,
      overflowY,
      cursor,
      zIndex,
      shadow,
      className,
      style,
      children,
      ...rest
    },
    ref,
  ) => {
    const generateDynamicClass = (
      type: string,
      value: string | '-1' | undefined,
    ) => {
      if (!value) return undefined;

      if (value === 'transparent') {
        return `transparent-border`;
      }

      if (value === 'surface' || value === 'page' || value === 'transparent') {
        return `${value}-${type}`;
      }

      const parts = value.split('-');
      if (parts.includes('alpha')) {
        const [scheme, , weight] = parts;
        return `${scheme}-${type}-alpha-${weight}`;
      }

      const [scheme, weight] = value.split('-') as [ColorScheme, ColorWeight];
      return `${scheme}-${type}-${weight}`;
    };

    const parseDimension = (
      value: number | SpacingToken | undefined,
      type: 'width' | 'height',
    ): string | undefined => {
      if (value === undefined) return undefined;
      if (typeof value === 'number') return `${value}rem`;
      if (
        [
          '0',
          '1',
          '2',
          '4',
          '8',
          '12',
          '16',
          '20',
          '24',
          '32',
          '40',
          '48',
          '56',
          '64',
          '80',
          '104',
          '128',
          '160',
        ].includes(value)
      ) {
        return `var(--static-space-${value})`;
      }
      if (['xs', 's', 'm', 'l', 'xl'].includes(value)) {
        return `var(--responsive-${type}-${value})`;
      }
      return undefined;
    };

    const classes = classNames(
      inline ? 'display-inline-grid' : 'display-grid',
      fit && 'fit',
      fitWidth && 'fit-width',
      fitHeight && 'fit-height',
      fill && 'fill',
      (fillWidth || maxWidth) && 'fill-width',
      (fillHeight || maxHeight) && 'fill-height',
      columns && `columns-${columns}`,
      tabletColumns && `tablet-columns-${tabletColumns}`,
      mobileColumns && `mobile-columns-${mobileColumns}`,
      overflow && `overflow-${overflow}`,
      overflowX && `overflow-x-${overflowX}`,
      overflowY && `overflow-y-${overflowY}`,
      padding && `p-${padding}`,
      paddingLeft && `pl-${paddingLeft}`,
      paddingRight && `pr-${paddingRight}`,
      paddingTop && `pt-${paddingTop}`,
      paddingBottom && `pb-${paddingBottom}`,
      paddingX && `px-${paddingX}`,
      paddingY && `py-${paddingY}`,
      margin && `m-${margin}`,
      marginLeft && `ml-${marginLeft}`,
      marginRight && `mr-${marginRight}`,
      marginTop && `mt-${marginTop}`,
      marginBottom && `mb-${marginBottom}`,
      marginX && `mx-${marginX}`,
      marginY && `my-${marginY}`,
      gap && `g-${gap}`,
      top && `top-${top}`,
      right && `right-${right}`,
      bottom && `bottom-${bottom}`,
      left && `left-${left}`,
      generateDynamicClass('background', background),
      generateDynamicClass('solid', solid),
      generateDynamicClass(
        'border',
        border || borderTop || borderRight || borderBottom || borderLeft,
      ),
      (border || borderTop || borderRight || borderBottom || borderLeft) &&
        !borderStyle &&
        'border-solid',
      border && !borderWidth && `border-1`,
      (borderTop || borderRight || borderBottom || borderLeft) &&
        'border-reset',
      borderTop && 'border-top-1',
      borderRight && 'border-right-1',
      borderBottom && 'border-bottom-1',
      borderLeft && 'border-left-1',
      borderWidth && `border-${borderWidth}`,
      borderStyle && `border-${borderStyle}`,
      radius === 'full' ? 'radius-full' : radius && `radius-${radius}`,
      topRadius && `radius-${topRadius}-top`,
      rightRadius && `radius-${rightRadius}-right`,
      bottomRadius && `radius-${bottomRadius}-bottom`,
      leftRadius && `radius-${leftRadius}-left`,
      topLeftRadius && `radius-${topLeftRadius}-top-left`,
      topRightRadius && `radius-${topRightRadius}-top-right`,
      bottomLeftRadius && `radius-${bottomLeftRadius}-bottom-left`,
      bottomRightRadius && `radius-${bottomRightRadius}-bottom-right`,
      hide === 's' && `${hide}-grid-hide`,
      show === 's' && `${show}-grid-show`,
      pointerEvents && `pointer-events-${pointerEvents}`,
      transition && `transition-${transition}`,
      shadow && `shadow-${shadow}`,
      position && `position-${position}`,
      zIndex && `z-index-${zIndex}`,
      textType && `font-${textType}`,
      cursor && `cursor-${cursor}`,
      dark && 'dark-grid',
      light && 'light-grid',
      className,
    );

    const combinedStyle: CSSProperties = {
      maxWidth: parseDimension(maxWidth, 'width'),
      minWidth: parseDimension(minWidth, 'width'),
      minHeight: parseDimension(minHeight, 'height'),
      maxHeight: parseDimension(maxHeight, 'height'),
      width: parseDimension(width, 'width'),
      height: parseDimension(height, 'height'),
      aspectRatio: aspectRatio,
      textAlign: align,
      ...style,
    };

    return (
      <Component ref={ref} className={classes} style={combinedStyle} {...rest}>
        {children}
      </Component>
    );
  },
);

Grid.displayName = 'Grid';

export { Grid };
export type { GridProps };
